import { useProjectAddCustomClassMutation } from '../../../types/graphqlTypes';
import { memo, useCallback, useContext, useMemo } from 'react';
import { useClassificationTranslations } from '../../../hooks/translations/useClassifications';
import { IClassification, useClassifications } from '../../../hooks/potree/useRenderer';
import { ComboBox, Divider, Option } from '../../../components/inputs/ComboBox';
import { stringToColor } from '../../../utils/stringToColor';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import useOpen from '../../../hooks/useOpen';
import { ClassificationSettingsModal } from '../../user/ClassificationSettingsModal';
import { RendererContext } from '../../../contexts/RendererContext';
import { PropertyValueType, type CustomPropertyInputProps } from '@pointorama/potree-shared';

export const MINIMUM_CLASS_INDEX = 64; // Lower indices are reserved in las/laz

interface ClassPropertyComponentProps {
  label: string;
  value: IClassification;
  onEdit: (value: IClassification) => void;
}
const ClassPropertyComponent_: React.FC2<ClassPropertyComponentProps> = ({ label, value, onEdit }) => {
  const isCustomClass = value.custom;

  return (
    <div className="flex items-center w-full overflow-hidden">
      <Option key={value.code} value={value} label={value.name} className="flex flex-grow min-w-0">
        <div className="flex items-center w-full min-w-0">
          <div
            className="flex-shrink-0 w-4 h-4 mr-1 border border-gray-500 rounded"
            style={{
              backgroundColor: `rgb(${value.color.map((c: number) => c * 255).join(', ')})`,
            }}
          />
          <div className="truncate">{label}</div>
        </div>
      </Option>
      {isCustomClass && (
        <div className="flex-shrink-0 m-2">
          <Cog6ToothIcon
            className="w-4 h-4 text-[#6F7173] hover:text-black dark:hover:text-neon-green-300"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(value);
            }}
          />
        </div>
      )}
    </div>
  );
};
const ClassPropertyComponent = memo(ClassPropertyComponent_);

const COMBOBOX_NAME = 'classification';

/**
 * Custom UI for the `annotation_class` reactive property.
 * Registered via propertyRegistry.setCustomComponent so the auto-generated
 * PropertyPanel renders this combobox instead of the default numeric input.
 *
 * `instance.value` holds the classification code (number); we resolve it to an
 * IClassification, render the colored picker, and emit the new code via onChange.
 * Adding a new custom class is a project-scoped mutation, hence the projectId prop.
 */
const ClassifyPropertyComponent_: React.FC<CustomPropertyInputProps<PropertyValueType.INT>> = ({ instance, onChange, disabled, projectId }) => {
  const classification = instance.value;

  const [classifications] = useClassifications();
  const [addCustomClass] = useProjectAddCustomClassMutation();
  const classificationTranslations = useClassificationTranslations();
  const viewer = useContext(RendererContext).viewer;

  const { onOpenWithValue: openClassificationSettingsModal, onClose: closeClassificationSettingsModal, open: ClassificationSettingsModalOpen, value: customClassification } = useOpen<IClassification>();

  const { classificationOptions, customClassificationOptions } = useMemo(() => {
    const classificationOptions = classifications.classifications.filter((c) => !c.custom);
    const customClassificationOptions = classifications.classifications.filter((c) => c.custom);
    return { classificationOptions, customClassificationOptions };
  }, [classifications]);

  const allClassificationOptions = useMemo(() => {
    return [...classificationOptions, ...customClassificationOptions];
  }, [classificationOptions, customClassificationOptions]);

  const currentValue = allClassificationOptions.find((option) => Number(option.code) === classification);

  const handleSelect = useCallback(
    (event: { target: { value: IClassification; name: string } }) => {
      onChange(event.target.value.code);
    },
    [onChange],
  );

  const onCustomClass = useCallback(
    async (customClassName: string) => {
      if (!projectId) return;
      const customClassResponse = await addCustomClass({
        variables: {
          projectId,
          color: { ...stringToColor(customClassName), a: 1 },
          name: customClassName,
        },
      });
      const customClass = customClassResponse.data?.projectAddCustomClass.customClasses?.find((c) => c.name === customClassName);
      if (!customClass) return;

      // Mirror eagerly so the dropdown sees the new code before syncClassifications catches up.
      const { r, g, b, a } = customClass.color;
      viewer?.classificationsInfo.addCustomClass({
        identifier: customClass.id,
        name: customClass.name,
        code: customClass.code,
        color: [r / 255, g / 255, b / 255, a],
      });
      viewer?.dispatchEvent({ type: 'classifications_data_changed' });

      onChange(customClass.code);
    },
    [addCustomClass, projectId, onChange, viewer],
  );

  return (
    <>
      <ComboBox<IClassification> className="w-full" onChange={handleSelect} value={currentValue} name={COMBOBOX_NAME} onCustomValue={onCustomClass} disabled={disabled}>
        {classificationOptions.map((value) => (
          <ClassPropertyComponent label={classificationTranslations[value.name]} value={value} onEdit={openClassificationSettingsModal} key={value.code} />
        ))}
        {customClassificationOptions.length > 0 && <Divider />}
        {customClassificationOptions.map((customClass) => (
          <ClassPropertyComponent label={customClass.name} value={customClass} onEdit={openClassificationSettingsModal} key={customClass.code} />
        ))}
      </ComboBox>
      {customClassification && projectId && <ClassificationSettingsModal onClose={closeClassificationSettingsModal} open={ClassificationSettingsModalOpen} projectId={projectId} value={customClassification} />}
    </>
  );
};
export const ClassifyPropertyComponent = memo(ClassifyPropertyComponent_);
